export const PreferenceKeys = [
	'analysis.currency',
	'analysis.filter.open',
	'tutorial.exports.location',
	'tutorial.calculations.location',
] as const;
export type PreferenceKey = (typeof PreferenceKeys)[number];

const DefaultItemValue: { [key in PreferenceKey]: string } = {
	'analysis.currency': 'USD',
	'analysis.filter.open': 'false',
	'tutorial.exports.location': 'false',
	'tutorial.calculations.location': 'false',
};

type PreferenceType = string | number | object | boolean;
export class Preference {
	protected value: PreferenceType | null = null;

	constructor(private readonly key: PreferenceKey) {}

	/**
	 * @param key the key of the preference to set
	 * @param value the value to set the preference to
	 */
	public set(value: PreferenceType) {
		this.value = value ?? null;
		if (typeof value === 'undefined') return;

		if (typeof value !== 'string') {
			value = JSON.stringify(value);
		}

		localStorage.setItem(this.key, value);
		return value;
	}

	/**
	 * Returns the value of the preference requested.
	 *
	 * @note type is detected based on the nature of the value. If the value can
	 * be parsed as a number, it will be returned as a number and same with boolean,
	 * object, and array. This is important to note because even if a number was initially
	 * saved as a string, it will still return as a number
	 *
	 * @param key the key of the preference to return
	 */
	public get(): PreferenceType | null {
		let value: PreferenceType | null =
			localStorage.getItem(this.key) ?? DefaultItemValue[this.key];

		if (value == null || value === 'null' || value === 'undefined') {
			value = null;
		} else if (
			value === 'true' ||
			value === 'false' ||
			(value.startsWith('{') && value.endsWith('}')) ||
			(value.startsWith('[') && value.endsWith(']'))
		) {
			value = JSON.parse(value);
		} else if (isNumericString(value)) {
			value = Number(value);
		} else {
			value = String(value);
		}

		this.value = value;
		return value;
	}

	public reset() {
		this.value = null;
		return null;
	}

	public readonly toString = () => String(this.value);

	public readonly valueOf = () => this.value ?? this.get();
}

function isNumericString(value: string) {
	const trimmed = value.trim();
	return trimmed !== '' && Number.isFinite(Number(trimmed));
}
